import { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const inputCls =
  'w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground placeholder:text-muted focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)]';

interface Wrap { name: string; pre: string; post: string; glyph: string }

const ACTIONS: Wrap[] = [
  { name: 'Bold', pre: '**', post: '**', glyph: 'B' },
  { name: 'Italic', pre: '_', post: '_', glyph: 'I' },
  { name: 'Heading', pre: '## ', post: '', glyph: 'H' },
  { name: 'List', pre: '- ', post: '', glyph: '•' },
  { name: 'Link', pre: '[', post: '](url)', glyph: '🔗' },
];

/**
 * MarkdownEditor — a textarea with a small Markdown toolbar (Bold, Italic,
 * Heading, List, Link) that wraps/prefixes the current selection, plus a
 * Preview toggle rendering the value via react-markdown + remark-gfm.
 *
 * Controlled: the parent owns `value` and receives every change via `onChange`.
 */
export function MarkdownEditor({
  id,
  label,
  value,
  onChange,
  rows = 4,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  const [preview, setPreview] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);

  const apply = (w: Wrap) => {
    const el = ref.current;
    let start = el?.selectionStart ?? value.length;
    let end = el?.selectionEnd ?? value.length;
    // No active selection (collapsed caret) → operate on the whole value, so a
    // bare click on a toolbar button formats the entire field.
    if (start === end) {
      start = 0;
      end = value.length;
    }
    const sel = value.slice(start, end);
    onChange(value.slice(0, start) + w.pre + sel + w.post + value.slice(end));
  };

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label htmlFor={id} className="block text-xs uppercase tracking-[0.06em] text-muted">
          {label}
        </label>
        <div className="flex items-center gap-1">
          {ACTIONS.map((a) => (
            <button
              key={a.name}
              type="button"
              aria-label={a.name}
              onClick={() => apply(a)}
              className="rounded px-1.5 py-0.5 text-xs text-muted hover:bg-[var(--surface-sunken)]"
            >
              {a.glyph}
            </button>
          ))}
          <button
            type="button"
            aria-label="Preview"
            onClick={() => setPreview((p) => !p)}
            className="rounded px-1.5 py-0.5 text-xs text-muted hover:bg-[var(--surface-sunken)]"
          >
            {preview ? 'Edit' : 'Preview'}
          </button>
        </div>
      </div>
      {preview ? (
        <div className="prose prose-sm max-w-none rounded-[var(--radius-control)] border border-border bg-card p-3 text-sm">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
        </div>
      ) : (
        <textarea
          id={id}
          ref={ref}
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      )}
    </div>
  );
}
